import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox, Fieldset, FormElement, Input, Label } from '@feathr/components';
import DateRangeSelect from '@feathr/extender/components/DateRangeSelect';
import { flattenError, getIconForAction } from '@feathr/hooks';

import { getInteractionTypeOptions } from '../GroupExportModal.utils';
import type { IEmailActivityTabState } from './EmailActivityTab.hooks';

export interface IEmailActivityTabProps {
  state: IEmailActivityTabState;
}

function EmailActivityTab({ state }: Readonly<IEmailActivityTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    exportName,
    exportNameHadFocus,
    exportNameError,
    email,
    emailHadFocus,
    emailError,
    selectedDateRange,
    customDateRange,
    dateRangeHadFocus,
    dateRangeOptions,
    dateRangeError,
    selectedInteractionTypes,
    interactionTypesError,
    handleChangeExportName,
    handleExportNameBlur,
    handleChangeEmail,
    handleEmailBlur,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleToggleInteractionType,
  } = state;

  const exportNameValidationError = exportNameHadFocus ? flattenError(exportNameError) : undefined;
  const emailValidationError = emailHadFocus ? flattenError(emailError) : undefined;
  const dateRangeValidationError = dateRangeHadFocus ? dateRangeError : undefined;

  const interactionTypeOptions = getInteractionTypeOptions(t);

  const interactionTypeTooltip = (
    <Trans t={t}>
      <p>
        <strong>Delivery:</strong> the email reached the recipient
      </p>
      <p>
        <strong>Open:</strong> the recipient opened the email
      </p>
      <p>
        <strong>Click:</strong> the recipient clicked a link in the email
      </p>
    </Trans>
  );

  const interactionTypeLabel = (
    <Label
      margin={'condensed'}
      tooltip={interactionTypeTooltip}
      tooltipIcon={getIconForAction('question')}
    >
      {t('Email interaction type')}
    </Label>
  );

  return (
    <Fieldset>
      <Stack gap={theme.spacing['4']}>
        <Input
          isClearable={true}
          label={t('Export name')}
          onBlur={handleExportNameBlur}
          onChange={handleChangeExportName}
          type={'text'}
          validationError={exportNameValidationError}
          value={exportName}
        />
        <FormElement
          label={interactionTypeLabel}
          validationError={flattenError(interactionTypesError)}
        >
          <Stack gap={theme.spacing['2']}>
            {interactionTypeOptions.map((option) => (
              <Checkbox
                key={option.id}
                label={option.label}
                onChange={(): void => handleToggleInteractionType(option.id)}
                value={selectedInteractionTypes.includes(option.id)}
              />
            ))}
          </Stack>
        </FormElement>
        <DateRangeSelect
          customDateRange={customDateRange}
          dateRangeOptions={dateRangeOptions}
          label={t('Time frame')}
          menuPortalTarget={document.body}
          name={'time-frame'}
          onCustomDateRangeChange={handleCustomDateRangeChange}
          onDateRangeChange={handleDateRangeChange}
          selectedDateRange={selectedDateRange}
          validationError={dateRangeValidationError}
        />
        <Input
          helpText={t('If you want to send the report to a different email address.')}
          isClearable={true}
          label={t('Email address')}
          onBlur={handleEmailBlur}
          onChange={handleChangeEmail}
          type={'text'}
          validationError={emailValidationError}
          value={email}
        />
      </Stack>
    </Fieldset>
  );
}

export default EmailActivityTab;
